// frontend/src/utils/transformRules.js
/**
 * Catálogo de transformaciones de DatiaFlow — FUENTE ÚNICA DE VERDAD.
 *
 * Cada entrada define: id, category, params[], label (fallback ES).
 * El label es texto de respaldo; la UI usa t('wizard.mapping.rule_names.${id}') primero.
 *
 * Los IDs deben coincidir exactamente con:
 *   - Frontend: ruleCatalog derivado en etlMappingUtils.js / RuleBuilderModal.jsx
 *   - Backend:  apply_transformations() en services/transformer.py
 *
 * Al agregar una transformación nueva:
 *   1. Agregarla aquí (id + category + params + label)
 *   2. Implementar en transformer.py (backend)
 *   3. Agregar UI en RuleBuilderModal.jsx (renderRuleInputs) si needsParams
 *   4. Agregar validación en getIncompleteRulesCount() de etlMappingUtils.js
 *   5. Agregar claves i18n en es.json / en.json / pt.json
 *   6. La validación de import/export se actualiza automáticamente (usa KNOWN_TRANSFORM_IDS)
 *
 * ⚠️  etlMappingUtils.js DERIVA su ruleCatalog desde este array.
 *     NO duplicar definiciones allá. Un solo lugar, un solo mantenimiento.
 */

export const TRANSFORM_RULES = [
  // ── Texto: limpieza (category: text) ─────────────────────────────
  { id: 'TRIM_EDGES', category: 'text', params: [], label: 'Quitar espacios a los extremos' },
  { id: 'COLLAPSE_SPACES', category: 'text', params: [], label: 'Quitar espacios repetidos' },
  { id: 'REMOVE_SPECIAL_CHARS', category: 'text', params: [], label: 'Quitar caracteres especiales (Dejar alfanuméricos)' },
  { id: 'REMOVE_ACCENTS', category: 'text', params: ['preserveÑ'], label: 'Quitar tildes/acentos (preserva ñ)' },
  { id: 'NORMALIZE_TEXT', category: 'text', params: [], label: 'Normalizar texto (trim + minúsculas + sin dobles espacios)' },
  // ── Texto: formato de letras ─────────────────────────────────────
  { id: 'UPPERCASE', category: 'text', params: [], label: 'Convertir a MAYÚSCULAS' },
  { id: 'LOWERCASE', category: 'text', params: [], label: 'Convertir a minúsculas' },
  { id: 'PROPER', category: 'text', params: [], label: 'Capitalizar Cada Palabra (Nombres)' },
  { id: 'CAPITALIZE_FIRST', category: 'text', params: [], label: 'Capitalizar solo primera letra' },
  // ── Texto: extracción ────────────────────────────────────────────
  { id: 'EXTRACT_NUMBERS', category: 'text', params: [], label: 'Extraer solo números del texto' },
  { id: 'SUBSTRING', category: 'text', params: ['start', 'end'], label: 'Extraer texto por posición (Desde - Hasta)' },
  { id: 'EXTRACT_END', category: 'text', params: ['length'], label: 'Extraer últimos N caracteres' },
  { id: 'SPLIT_GET', category: 'text', params: ['part', 'delimiter'], label: 'Extraer texto delimitado (Antes/Después de...)' },
  { id: 'SPLIT_FIELD', category: 'text', params: ['separator', 'index'], label: 'Dividir por separador y obtener parte N' },
  // ── Texto: modificación ──────────────────────────────────────────
  { id: 'ADD_PREFIX', category: 'text', params: ['text'], label: 'Añadir texto al inicio (Prefijo)' },
  { id: 'ADD_SUFFIX', category: 'text', params: ['text'], label: 'Añadir texto al final (Sufijo)' },
  { id: 'PAD_LEFT', category: 'text', params: ['padChar', 'total_length'], label: 'Rellenar caracteres a la izquierda' },
  { id: 'PAD_RIGHT', category: 'text', params: ['padChar', 'total_length'], label: 'Rellenar caracteres a la derecha' },
  { id: 'TRUNCATE', category: 'text', params: ['max_length'], label: 'Limitar longitud máxima (Truncar)' },
  { id: 'FORMAT_CODE', category: 'text', params: ['prefix', 'padChar', 'totalLength'], label: 'Crear código con prefijo + relleno (PRD00abc)' },
  { id: 'STRIP_CODE', category: 'text', params: ['prefix', 'stripLeadingZeros'], label: 'Quitar prefijo y ceros iniciales de código' },
  // ── Numérico: limpieza y conversión (category: number) ──────────
  { id: 'REMOVE_CURRENCY', category: 'number', params: [], label: 'Quitar símbolos de moneda y comas' },
  { id: 'TO_INT', category: 'number', params: [], label: 'Convertir a Entero (Quitar decimales)' },
  { id: 'TO_DECIMAL', category: 'number', params: [], label: 'Convertir a Decimal' },
  // ── Numérico: matemáticas (category: number) ────────────────────
  { id: 'MATH_ROUND', category: 'number', params: ['decimals'], label: 'Redondear decimales' },
  { id: 'MATH_ADD_SUB', category: 'number', params: ['value'], label: 'Sumar o Restar valor' },
  { id: 'MATH_MULTIPLY', category: 'number', params: ['value'], label: 'Multiplicar por' },
  { id: 'MATH_DIVIDE', category: 'number', params: ['value'], label: 'Dividir entre' },
  // ── Fechas (category: date) ──────────────────────────────────────
  { id: 'FORMAT_DATE', category: 'date', params: ['from', 'to', 'custom_from', 'custom_to'], label: 'Cambiar formato de Fecha' },
  { id: 'DATE_EXTRACT', category: 'date', params: ['part'], label: 'Extraer Año / Mes / Día' },
  { id: 'DATE_NOW', category: 'date', params: [], label: 'Asignar fecha y hora actual (NOW)' },
  { id: 'SET_CURRENT_DATE', category: 'date', params: ['dateFormat', 'offsetDays'], label: 'Fecha actual con formato y desplazamiento' },
  // ── Condicionales (category: cond) ───────────────────────────────
  { id: 'EMPTY_TO_NULL', category: 'cond', params: [], label: 'Convertir vacío a Nulo (NULL de base de datos)' },
  { id: 'DEFAULT_NULL', category: 'cond', params: ['detectType', 'value'], label: 'Reemplazar celdas vacías/nulas con valor fijo' },
  { id: 'REPLACE', category: 'cond', params: ['search', 'replace'], label: 'Buscar y Reemplazar palabra' },
  { id: 'ADVANCED_COND', category: 'cond', params: ['operator', 'condition_val', 'result', 'else_action', 'else_result'], label: 'Condición Avanzada (Si... Entonces)' },
  { id: 'TRANSLATE_VALUE', category: 'cond', params: ['mappings', 'default'], label: 'Tabla de equivalencias (M→Masculino)' },
  // ── Valor fijo (category: fixed) ─────────────────────────────────
  { id: 'SET_FIXED', category: 'fixed', params: ['value'], label: 'Asignar valor fijo a toda la columna' },
  // ── Seguridad (category: security) ───────────────────────────────
  { id: 'MASK_EMAIL', category: 'security', params: [], label: 'Ocultar Email (j***@mail.com)' },
  { id: 'MASK_LAST_4', category: 'security', params: [], label: 'Ocultar todo excepto últimos 4 (Tarjetas)' },
  { id: 'MASK_ALL', category: 'security', params: [], label: 'Ocultar todo con asteriscos (***)' },
  { id: 'HASH_DATA', category: 'security', params: [], label: 'Encriptar dato (Hash SHA-256)' },
  // ── Lookup en BD destino (category: lookup) ──────────────────────
  { id: 'LOOKUP', category: 'lookup', params: ['lookupTable', 'lookupField', 'returnField', 'onNotFound', 'defaultValue', 'filterField', 'filterSource', 'filterValue'], label: 'Buscar ID en tabla destino (Lookup)' },
  // ── JSON ──────────────────────
  { id: 'JSON_EXTRACT', category: 'extract', params: ['path'], label: 'Extraer campo de JSON anidado' },
];

/**
 * Ejemplos por regla — input/output + nota siempre presente.
 * Usado por RuleBuilderModal para el chip 💡 de ayuda contextual.
 *
 * Campos:
 *   input       : dato que entra (solo el valor, nunca parámetros de configuración)
 *   output      : resultado (limpio)
 *   note        : explicación clara. SIEMPRE presente.
 *   inputLabel  : (opcional) contexto del dato de entrada — solo para reglas conceptuales
 *   outputLabel : (opcional) contexto del resultado — solo para reglas conceptuales
 */
export const RULE_EXAMPLES = {
  // ═══════════════════════════════════════════════════════════════════
  // TEXTO: LIMPIEZA
  // ═══════════════════════════════════════════════════════════════════
  TRIM_EDGES: {
    input: '"  Juan Pérez  "',
    output: '"Juan Pérez"',
    note: 'Elimina espacios al inicio y final. No afecta espacios entre palabras.',
  },
  COLLAPSE_SPACES: {
    input: '"Juan    Pérez   López"',
    output: '"Juan Pérez López"',
    note: 'Reduce múltiples espacios consecutivos a uno solo. Útil cuando los datos vienen de copiar y pegar.',
  },
  REMOVE_SPECIAL_CHARS: {
    input: '"Código#123-ABC!"',
    output: '"Cdigo123ABC"',
    note: 'Deja solo letras (a-z) y números (0-9). Elimina guiones, signos, tildes y todo carácter no alfanumérico.',
  },
  REMOVE_ACCENTS: {
    input: '"María José Muñoz"',
    output: '"Maria Jose Muñoz"',
    note: 'Quita tildes y acentos pero conserva la ñ. Útil cuando el destino no acepta caracteres acentuados.',
  },
  NORMALIZE_TEXT: {
    input: '"  JUAN   pérez  "',
    output: '"juan pérez"',
    note: 'Aplica 3 limpiezas a la vez: quita espacios extremos, pasa a minúsculas y elimina espacios dobles.',
  },

  // ═══════════════════════════════════════════════════════════════════
  // TEXTO: FORMATO DE LETRAS
  // ═══════════════════════════════════════════════════════════════════
  UPPERCASE: {
    input: '"Juan Pérez"',
    output: '"JUAN PÉREZ"',
    note: 'Convierte todas las letras a mayúsculas. El texto original no se modifica en el origen.',
  },
  LOWERCASE: {
    input: '"Juan Pérez"',
    output: '"juan pérez"',
    note: 'Convierte todas las letras a minúsculas.',
  },
  PROPER: {
    input: '"juan carlos pérez"',
    output: '"Juan Carlos Pérez"',
    note: 'Pone en mayúscula la primera letra de cada palabra. Ideal para nombres de personas.',
  },
  CAPITALIZE_FIRST: {
    input: '"juan pérez lópez"',
    output: '"Juan pérez lópez"',
    note: 'Solo la primera letra de todo el texto pasa a mayúscula. El resto queda igual.',
  },

  // ═══════════════════════════════════════════════════════════════════
  // TEXTO: EXTRACCIÓN
  // ═══════════════════════════════════════════════════════════════════
  EXTRACT_NUMBERS: {
    input: '"Fact-2024-00123"',
    output: '"202400123"',
    note: 'Extrae solo los dígitos y descarta letras, guiones y otros caracteres. Útil para limpiar códigos de documentos.',
  },
  SUBSTRING: {
    input: '"PE-20512345678"',
    output: '"20512345678"',
    note: 'Extrae un fragmento del texto indicando posición inicial y final. En este ejemplo: desde la posición 3 hasta la 14.',
  },
  EXTRACT_END: {
    input: '"INV-2024-0589"',
    output: '"0589"',
    note: 'Extrae los últimos N caracteres del texto. En este ejemplo: los últimos 4.',
  },
  SPLIT_GET: {
    input: '"abc|def"',
    output: '"def"',
    note: 'Divide el texto por un separador y toma la parte que elijas (antes o después). En este ejemplo: lo que está después de "|".',
  },
  SPLIT_FIELD: {
    input: '"abcd;efghi;jklm;nopqr"',
    output: '"jklm"',
    note: 'Divide el texto por un separador y toma la parte N (la cuenta empieza en 0). En este ejemplo: separador ";", parte 2 = "jklm".',
  },

  // ═══════════════════════════════════════════════════════════════════
  // TEXTO: MODIFICACIÓN
  // ═══════════════════════════════════════════════════════════════════
  ADD_PREFIX: {
    input: '"12345"',
    output: '"CLI-12345"',
    note: 'Agrega un texto fijo al inicio del valor. En este ejemplo: el prefijo "CLI-".',
  },
  ADD_SUFFIX: {
    input: '"Alex"',
    output: '"Alex-II"',
    note: 'Agrega un texto fijo al final del valor. En este ejemplo: el sufijo "-II".',
  },
  PAD_LEFT: {
    input: '"42"',
    output: '"000042"',
    note: 'Rellena con un carácter a la izquierda hasta alcanzar el largo deseado. En este ejemplo: carácter "0", largo total 6. Útil para códigos de longitud fija.',
  },
  PAD_RIGHT: {
    input: '"AB"',
    output: '"ABXXX"',
    note: 'Rellena con un carácter a la derecha hasta alcanzar el largo deseado. En este ejemplo: carácter "X", largo total 5.',
  },
  TRUNCATE: {
    input: '"Descripción muy larga del producto"',
    output: '"Descripción"',
    note: 'Corta el texto si excede el largo máximo permitido. En este ejemplo: máximo 12 caracteres.',
  },
  FORMAT_CODE: {
    input: '"abc"',
    output: '"PRD00abc"',
    note: 'Crea un código estandarizado combinando un prefijo fijo, caracteres de relleno y el valor original. En este ejemplo: prefijo "PRD", relleno con "0", largo total 8.',
  },
  STRIP_CODE: {
    input: '"PRD00abc"',
    output: '"abc"',
    note: 'Operación inversa a "Crear código": quita el prefijo y opcionalmente los ceros iniciales para recuperar el valor original.',
  },

  // ═══════════════════════════════════════════════════════════════════
  // NUMÉRICO
  // ═══════════════════════════════════════════════════════════════════
  REMOVE_CURRENCY: {
    input: '"$ 1,234.56"',
    output: '"1234.56"',
    note: 'Quita símbolos de moneda ($, €, S/.) y separadores de miles. Deja solo el número limpio para que el destino lo acepte como valor numérico.',
  },
  TO_INT: {
    input: '"99.7"',
    output: '"99"',
    note: 'Elimina la parte decimal sin redondear. El valor 99.7 se convierte en 99, no en 100.',
  },
  TO_DECIMAL: {
    input: '"100"',
    output: '"100.0"',
    note: 'Convierte un número entero a formato decimal. Útil cuando el destino exige campos numéricos con decimales.',
  },
  MATH_ROUND: {
    input: '"3.14159"',
    output: '"3.14"',
    note: 'Redondea el número a la cantidad de decimales que configures. En este ejemplo: 2 decimales.',
  },
  MATH_ADD_SUB: {
    input: '"100"',
    output: '"82"',
    note: 'Suma o resta un valor fijo al número. En este ejemplo: se resta 18 (configurando el valor como -18). Usa número negativo para restar.',
  },
  MATH_MULTIPLY: {
    input: '"50.00"',
    output: '"59.00"',
    note: 'Multiplica el número por un valor fijo. En este ejemplo: × 1.18. Útil para agregar impuestos como el IGV.',
  },
  MATH_DIVIDE: {
    input: '"59.00"',
    output: '"50.00"',
    note: 'Divide el número entre un valor fijo. En este ejemplo: ÷ 1.18. Si el divisor es 0, el campo queda vacío para evitar errores.',
  },

  // ═══════════════════════════════════════════════════════════════════
  // FECHAS
  // ═══════════════════════════════════════════════════════════════════
  FORMAT_DATE: {
    input: '"2024-03-15"',
    output: '"15/03/2024"',
    note: 'Cambia el formato de la fecha para que coincida con lo que espera el destino. En este ejemplo: de YYYY-MM-DD a DD/MM/YYYY.',
  },
  DATE_EXTRACT: {
    input: '"2024-03-15"',
    output: '"3"',
    note: 'Extrae solo una parte de la fecha: el año, el mes o el día. En este ejemplo: el mes (3 = marzo).',
  },
  DATE_NOW: {
    input: '(cualquier valor)',
    output: '"2026-06-18 10:30:00"',
    note: 'Ignora el valor original y lo reemplaza con la fecha y hora exacta en que se ejecuta la migración.',
  },
  SET_CURRENT_DATE: {
    input: '(cualquier valor)',
    output: '"17/06/2026"',
    note: 'Asigna la fecha actual con el formato que configures y un desplazamiento opcional en días. En este ejemplo: formato DD/MM/YYYY con offset -1 (ayer).',
  },

  // ═══════════════════════════════════════════════════════════════════
  // CONDICIONALES
  // ═══════════════════════════════════════════════════════════════════
  EMPTY_TO_NULL: {
    input: '""',
    output: 'NULL',
    inputLabel: 'campo de texto vacío',
    outputLabel: 'nulo en base de datos',
    note: 'Convierte cadenas vacías, espacios en blanco o valores nulos a NULL real de base de datos. Útil cuando el origen es texto y el destino es numérico: el vacío se convierte en NULL (aceptado), en vez de "" (rechazado). Si el campo tiene un valor válido como "123", lo deja pasar intacto.',
  },
  DEFAULT_NULL: {
    input: '(vacío)',
    output: '"0"',
    note: 'Si la celda está vacía o nula, la reemplaza con el valor fijo que configures. En este ejemplo: asigna "0". Útil para evitar campos vacíos que causan errores en el destino.',
  },
  REPLACE: {
    input: '"Dr. Alex Malta"',
    output: '"Doctor Juan Malta"',
    note: 'Busca un texto exacto dentro del valor y lo reemplaza por otro. En este ejemplo: cada vez que encuentra "Dr." lo cambia por "Doctor".',
  },
  ADVANCED_COND: {
    input: '"100"',
    output: '"SUPERIOR"',
    inputLabel: 'monto del pedido',
    outputLabel: 'categoría asignada',
    note: 'Evalúa una condición sobre el valor y asigna un resultado. En este ejemplo: si el monto es mayor a 100 escribe "SUPERIOR", si no escribe "INFERIOR". Útil para clasificar registros automáticamente.',
  },
  TRANSLATE_VALUE: {
    input: '"M"',
    output: '"Manual"',
    inputLabel: 'código corto del origen',
    outputLabel: 'texto completo para el destino',
    note: 'Reemplaza códigos cortos por su equivalencia usando una tabla que tú defines. En este ejemplo: M → Manual, A → Automático. Ideal para procesos o catálogos donde el origen usa abreviaturas y el destino necesita el texto completo.',
  },

  // ═══════════════════════════════════════════════════════════════════
  // VALOR FIJO
  // ═══════════════════════════════════════════════════════════════════
  SET_FIXED: {
    input: '(cualquier valor)',
    output: '"ACTIVO"',
    note: 'Ignora el valor original. Toda la columna recibe el mismo valor fijo que configures. Útil para campos de estado, marcas o valores por defecto que no existen en el origen.',
  },

  // ═══════════════════════════════════════════════════════════════════
  // SEGURIDAD
  // ═══════════════════════════════════════════════════════════════════
  MASK_EMAIL: {
    input: '"stella.corso@gmail.com"',
    output: '"s***@gmail.com"',
    note: 'Oculta el nombre del correo manteniendo solo la primera letra y el dominio. Útil para proteger datos personales al migrar.',
  },
  MASK_LAST_4: {
    input: '"4532-1234-5678-9012"',
    output: '"***************9012"',
    note: 'Oculta todo el contenido excepto los últimos 4 caracteres. Ideal para tarjetas de crédito o números de documento.',
  },
  MASK_ALL: {
    input: '"abcde fghijklmnop qrst"',
    output: '"**********************"',
    note: 'Reemplaza todo el contenido con asteriscos. El largo de asteriscos coincide con el largo original para mantener la estructura.',
  },
  HASH_DATA: {
    input: '"password123"',
    output: '"ef92b778bafe..."',
    note: 'Genera un código único e irreversible (SHA-256). No se puede recuperar el valor original a partir del resultado. Útil para contraseñas o datos que deben almacenarse cifrados.',
  },

  // ═══════════════════════════════════════════════════════════════════
  // LOOKUP
  // ═══════════════════════════════════════════════════════════════════
  LOOKUP: {
    input: '"Neumología"',
    output: '"47"',
    inputLabel: 'nombre de especialidad en el origen',
    outputLabel: 'ID de la especialidad en el destino',
    note: 'Busca automáticamente en una tabla del destino. Si el origen trae "Neumología", busca en la tabla de especialidades y devuelve su ID (47). Opcionalmente puedes filtrar la búsqueda (ej: buscar distrito solo dentro de un departamento). Si no encuentra equivalencia: dejar vacío, asignar valor por defecto, marcar error u omitir la fila.',
  },

  // ═══════════════════════════════════════════════════════════════════
  // JSON
  // ═══════════════════════════════════════════════════════════════════
  JSON_EXTRACT: {
    input: '\'{"address":{"city":"Lima","zip":"15001"}}\'',
    output: '"Lima"',
    inputLabel: 'campo JSON del origen',
    outputLabel: 'valor extraído',
    note: 'Extrae un valor anidado de un campo que contiene JSON. Usa notación con puntos: "address.city" extrae "Lima". Si la ruta no existe, el resultado es vacío.',
  },
};

/** Set de IDs para validación rápida — usado por mappingIO.js */
export const KNOWN_TRANSFORM_IDS = new Set(TRANSFORM_RULES.map(r => r.id));

/** Buscar definición de regla por ID */
export const getTransformRule = (id) => TRANSFORM_RULES.find(r => r.id === id);

/** Nombres de parámetros esperados por una regla */
export const getTransformParams = (id) => getTransformRule(id)?.params || [];
